import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { FlightsClassTypes, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useContext, useEffect, useMemo } from 'react'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchFlights } from 'actions/FlightActions'
import { getFlightSearchKey, getFlightSearchLuxPlusCacheKey, isSkyLoungeSingleOccupancy } from 'lib/flights/flightUtils'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { CHARTER } from 'constants/provider'
import { getIsEnabledFlightsBundledLuxPlusPricing } from 'luxPlus/selectors/featureToggle'
import { canRequestFlightLuxPlusPricing } from 'lib/flights/flightLuxPlusPricing'

export default function useAllCharterFlightJourneys({
  charterBundleRoute,
  occupancies = EmptyArray,
  fareClass,
  disabled,
  feederOriginAirportCode,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  occupancies: Array<App.Occupants>
  fareClass: FareClassWithReward
  disabled?: boolean
  /**
   * When set (and different from the charter origin airport), the user has selected a feeder
   * origin airport. Forwarded to /flightsearch so svc-flights can attach `feederEstimatedPrice`
   * on each fare from CharterFeederPrices FDA data (economy only).
   */
  feederOriginAirportCode?: string
}) {
  const routeInfo = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined
  const { currentCurrency } = useContext(GeoContext)
  const email = useAppSelector(state => state.auth.account.email)
  const bundledLuxPlusPricingEnabled = useAppSelector(getIsEnabledFlightsBundledLuxPlusPricing)
  const requestLuxPlusPricing = useAppSelector(state => canRequestFlightLuxPlusPricing(state, {
    enabled: bundledLuxPlusPricingEnabled,
  }))

  // Use lux_row_pax_one for single occupancy Economy Extra bookings (1 adult or 1 adult + 1 infant)
  const effectiveFareClass = useMemo(() => {
    const baseFareClass = toBaseFareClass(fareClass)
    if (baseFareClass === FlightsClassTypes.LUXROW && isSkyLoungeSingleOccupancy(occupancies)) {
      return FlightsClassTypes.LUXROW_PAX_ONE
    }
    return baseFareClass
  }, [fareClass, occupancies])

  const flightSearchArgs = useStableObjectReference<Parameters<typeof fetchFlights>[0] | undefined>(
    routeInfo ? {
      itinerary: [{
        departureAirport: routeInfo.originAirport.airportCode ?? '',
        arrivalAirport: routeInfo.destinationAirport.airportCode ?? '',
        departureDate: routeInfo.dateRange.min,
      },
      {
        departureAirport: routeInfo.destinationAirport.airportCode ?? '',
        arrivalAirport: routeInfo.originAirport.airportCode ?? '',
        departureDate: routeInfo.dateRange.max,
      }],
      occupancies,
      fareClass: effectiveFareClass,
      source: CHARTER,
      currency: currentCurrency,
      email,
      key: getFlightSearchLuxPlusCacheKey(undefined, requestLuxPlusPricing),
      ...(feederOriginAirportCode && feederOriginAirportCode !== routeInfo.originAirport.airportCode
        ? { feederOriginAirportCode }
        : {}),
    } : undefined,
  )

  const key = flightSearchArgs && getFlightSearchKey(flightSearchArgs)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!disabled && flightSearchArgs) {
      dispatch(fetchFlights(flightSearchArgs, {
        enableLuxPlusPricing: bundledLuxPlusPricingEnabled,
        isBundledFlow: true,
      }))
    }
  }, [bundledLuxPlusPricingEnabled, disabled, flightSearchArgs, dispatch])

  const flightsV2 = useAppSelector(state => key ? state.flights.searchV2Flights[key] : undefined)

  return flightsV2
}
